/**
 * 引导流程状态
 * ============================================================
 * 对应需求文档 V1.0 的 A 组页面（首次启动与引导）。
 *
 * ── 与文档的差异及原因（有意为之，不是遗漏）──────────────────
 *   A01 启动页       —— 跳过。Web 应用由浏览器负责加载，
 *                       加一个假启动屏只会拖延用户。
 *   A03 登录/注册    —— 跳过。本项目没有后端用户系统，
 *                       摆一个登不上去的登录按钮是欺骗用户。
 *                       文档自己也把「游客体验」列为可选路径。
 *   A06 口音/地区选择 —— 合并进 A05。我们的方言条目本身已带地区
 *                       （如「粤语（香港）」「四川话（成都）」），
 *                       再让用户选一次口音是重复劳动。
 *
 * 其余步骤按文档实现，全部数据存本机（与学习进度一致，不涉及账号）。
 */

const KEY = 'dialectgo_onboarding';

/** 学习目标（文档 A07 给的方向） */
export const GOALS = [
  { id: 'travel', label: '旅行沟通', icon: 'luggage', desc: '出门在外能听懂、能应付' },
  { id: 'family', label: '家庭交流', icon: 'family_restroom', desc: '和长辈、亲戚说家乡话' },
  { id: 'work', label: '工作沟通', icon: 'work', desc: '同事客户之间听得懂、说得上' },
  { id: 'culture', label: '文化兴趣', icon: 'theater_comedy', desc: '听歌看剧，理解当地文化' },
  { id: 'fun', label: '随便学学', icon: 'favorite', desc: '没有明确目标，先玩起来' },
];

/** 自评水平（文档 A08；不做测评题，先让用户自评，可跳过） */
export const LEVELS = [
  { id: 'zero', label: '完全零基础', desc: '一句都不会', courseLevel: 'beginner' },
  { id: 'some', label: '会一点点', desc: '能听懂零星词句', courseLevel: 'beginner' },
  { id: 'basic', label: '能简单对话', desc: '日常寒暄没问题', courseLevel: 'intermediate' },
  { id: 'good', label: '比较熟练', desc: '想提升地道程度', courseLevel: 'advanced' },
];

/** 每日目标时长（文档 A11） */
export const DAILY_PLANS = [
  { id: 'light', minutes: 5, label: '轻松', desc: '每天 5 分钟' },
  { id: 'normal', minutes: 15, label: '标准', desc: '每天 15 分钟' },
  { id: 'serious', minutes: 30, label: '认真', desc: '每天 30 分钟' },
];

const DEFAULT_STATE = {
  completed: false,
  nativeLanguage: 'zh-CN', // A04 母语
  dialectId: null, // A05 目标方言
  goals: [], // A07 学习目标（多选）
  level: null, // A08 自评水平
  tutorId: null, // A09 AI 教师
  planId: 'normal', // A11 每日计划
  micExplained: false, // A10 权限说明已读
  startedAt: null,
  finishedAt: null,
};

/** 读取引导状态 */
export function getOnboarding() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const j = JSON.parse(raw);
    return { ...DEFAULT_STATE, ...(j && typeof j === 'object' ? j : {}) };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

/** 保存（合并式） */
export function saveOnboarding(patch) {
  try {
    const next = { ...getOnboarding(), ...patch };
    localStorage.setItem(KEY, JSON.stringify(next));
    return next;
  } catch {
    return getOnboarding();
  }
}

/**
 * 是否需要跑引导
 * 首次启动（没有记录）或未完成过 → 需要
 */
export function needsOnboarding() {
  return !getOnboarding().completed;
}

/** 完成引导 */
export function completeOnboarding(patch = {}) {
  return saveOnboarding({ ...patch, completed: true, finishedAt: new Date().toISOString() });
}

/** 重跑引导（设置里用） */
export function resetOnboarding() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* 忽略 */
  }
}

/**
 * 把引导结果转成后续要用的默认值
 * 例如把自评水平映射成课程等级
 */
export function derivedPreferences() {
  const s = getOnboarding();
  const lv = LEVELS.find((l) => l.id === s.level);
  const plan = DAILY_PLANS.find((p) => p.id === s.planId) || DAILY_PLANS[1];
  return {
    dialectId: s.dialectId,
    tutorId: s.tutorId,
    goals: s.goals,
    level: s.level,
    courseLevel: lv?.courseLevel || 'beginner',
    dailyMinutes: plan.minutes,
  };
}
